import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';

/**
 * Preset CASSEMS sobre o Aura.
 *
 * Antes deste arquivo o projeto rodava com o Aura puro, e cada tela reescrevia a aparência dos
 * componentes com `!important` no seu próprio `<style>`. Era a causa raiz de 146 `!important`,
 * 50 cores hex soltas e da sensação de inconsistência entre telas.
 *
 * Aqui as decisões acontecem UMA vez: input, botão, tabela e diálogo já nascem com a identidade
 * CASSEMS, e as views voltam a tratar só do que é específico delas.
 *
 * Os valores espelham os tokens de assets/tokens.css. Como o PrimeVue resolve o preset em tempo
 * de execução (fora do escopo do CSS), os literais são repetidos aqui de propósito — mudar a cor
 * da marca exige tocar os dois arquivos, e é por isso que ambos citam a mesma origem.
 */
export const CassemsPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '10px',
      xl: '14px',
    },
  },

  semantic: {
    // Rampa Material Indigo — a do tema Ultima do legado. Espelha assets/tokens.css;
    // a marca é o 500 (#3F51B5, topbar do legado) e o 800 é o bloco do logo (#283593).
    primary: {
      50: '#e8eaf6',
      100: '#c5cae9',
      200: '#9fa8da',
      300: '#7986cb',
      400: '#5c6bc0',
      500: '#3f51b5',
      600: '#3949ab',
      700: '#303f9f',
      800: '#283593',
      900: '#1a237e',
      950: '#141b5c',
    },

    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '10px',
      xl: '14px',
    },

    // Anel de foco visível — o projeto não tinha nenhum, e navegar por teclado era às cegas.
    focusRing: {
      width: '3px',
      style: 'solid',
      color: 'rgba(63, 81, 181, 0.35)',
      offset: '1px',
      shadow: 'none',
    },

    // Densidade dos campos: tela de operação, então compacta mas com alvo de toque adequado.
    formField: {
      paddingX: '0.625rem',
      /* 0.375rem => controle de ~34px. Estas telas têm 30+ campos; cada 4px de altura de campo
         custa quase uma linha inteira de formulário na dobra. Continua acima do piso de 32px. */
      paddingY: '0.375rem',
      borderRadius: '4px',
      focusRing: {
        width: '3px',
        style: 'solid',
        color: 'rgba(63, 81, 181, 0.35)',
        offset: '0',
        shadow: 'none',
      },
      transitionDuration: '180ms',
    },

    content: {
      borderRadius: '6px',
    },

    colorScheme: {
      light: {
        primary: {
          color: '#3f51b5',
          contrastColor: '#ffffff',
          hoverColor: '#303f9f',
          activeColor: '#283593',
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '#f7f7f7',
          filledBackground: '#fafafa',
          borderColor: '#949494',
          hoverBorderColor: '#9e9e9e',
          focusBorderColor: '#3f51b5',
          invalidBorderColor: '#c62828',
          color: '#212121',
          disabledColor: '#9e9e9e',
          placeholderColor: '#757575',
          floatLabelColor: '#757575',
          floatLabelFocusColor: '#3f51b5',
          floatLabelActiveColor: '#757575',
          floatLabelInvalidColor: '#c62828',
          iconColor: '#757575',
          shadow: 'none',
        },
        content: {
          background: '#ffffff',
          hoverBackground: '#f7f7f7',
          borderColor: '#e8e8e8',
          color: '#212121',
          hoverColor: '#121212',
        },
        text: {
          color: '#212121',
          hoverColor: '#121212',
          mutedColor: '#616161',
          hoverMutedColor: '#424242',
        },
        overlay: {
          modal: {
            borderRadius: '6px',
            shadow: '0 4px 8px rgba(18,18,18,0.08), 0 12px 28px rgba(18,18,18,0.12)',
          },
        },
      },
    },
  },

  components: {
    button: {
      root: {
        borderRadius: '4px',
        paddingX: '0.875rem',
        // Mesma altura do campo (~34px): dentro de um InputGroup o botão fica colado ao input,
        // e 4px de diferença viram um degrau visível na linha de base.
        paddingY: '0.375rem',
        gap: '0.5rem',
        label: { fontWeight: '600' },
        // Sombra só no estado elevado; botão de formulário é plano.
        raisedShadow: '0 1px 2px rgba(18,18,18,0.06), 0 1px 3px rgba(18,18,18,0.08)',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '#3f51b5',
              hoverBackground: '#303f9f',
              activeBackground: '#283593',
              borderColor: '#3f51b5',
              hoverBorderColor: '#303f9f',
              activeBorderColor: '#283593',
              color: '#ffffff',
            },
          },
        },
      },
    },

    datatable: {
      headerCell: {
        padding: '0.625rem 0.75rem',
        fontWeight: '600',
      },
      bodyCell: {
        padding: '0.5rem 0.75rem',
      },
      colorScheme: {
        light: {
          headerCell: {
            background: '#3f51b5',
            color: '#ffffff',
            borderColor: '#303f9f',
            hoverBackground: '#303f9f',
            hoverColor: '#ffffff',
            // Coluna ordenada. Sem esta linha o Aura cai no {highlight.background} dele, um
            // azul quase branco — e como selectedColor ja era #ffffff, o cabecalho ordenado
            // ficava com texto branco sobre fundo claro. Mesmo tom do hover, para a coluna
            // ativa continuar na familia azul das demais.
            selectedBackground: '#303f9f',
            selectedColor: '#ffffff',
          },
          // O icone de ordenacao usa {text.muted.color} por padrao, um cinza pensado para
          // cabecalho claro. Sobre o azul ele fica sem contraste.
          sortIcon: {
            color: '#ffffff',
            hoverColor: '#ffffff',
          },
          row: {
            background: '#ffffff',
            stripedBackground: '#fafafa',
            hoverBackground: '#e8eaf6',
            color: '#212121',
            hoverColor: '#121212',
          },
          bodyCell: {
            borderColor: '#e8e8e8',
          },
        },
      },
    },

    dialog: {
      root: {
        borderRadius: '6px',
        shadow: '0 4px 8px rgba(18,18,18,0.08), 0 12px 28px rgba(18,18,18,0.12)',
      },
      header: { padding: '0.875rem 1.25rem' },
      content: { padding: '0 1.25rem 1.25rem 1.25rem' },
      footer: { padding: '0 1.25rem 1.25rem 1.25rem', gap: '0.5rem' },
    },

    panel: {
      root: { borderRadius: '6px' },
      header: { padding: '0.875rem 1.25rem' },
      content: { padding: '1.25rem' },
    },

    toast: {
      root: {
        borderRadius: '6px',
        shadow: '0 4px 8px rgba(18,18,18,0.08), 0 12px 28px rgba(18,18,18,0.12)',
      },
    },

    paginator: {
      root: {
        padding: '0.375rem 0.625rem',
        gap: '0.125rem',
        borderRadius: '0 0 6px 6px',
      },
      navButton: {
        borderRadius: '4px',
        width: '2rem',
        height: '2rem',
      },
    },

    tooltip: {
      root: { borderRadius: '4px' },
    },
  },
});

export default CassemsPreset;
